import Box from '@mui/material/Box';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { AppBar } from 'components/shell/app-bar';
import Search from 'components/views/search';
import { useFullHeightRoute } from 'hooks/use-full-height-route';
import { useQueryParamState } from 'hooks/use-query-params';
import { useAuthContext } from 'providers/auth';
import { PageContext } from 'providers/page';
import { useSnackbar } from 'providers/snackbar';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { FILL_PARENT_SX, FULL_HEIGHT_ROW_SX } from 'utils/layout';
import { deleteLocalStorageAuth } from 'utils/local-storage';
import {
  canSidebarPeek,
  isInsideCampaign,
  isSidebarPinned,
  readManualCollapsed,
  readSidebarMode,
  SidebarMode,
  writeManualCollapsed,
  writeSidebarMode,
} from 'utils/sidebar-mode';

const FULL_HEIGHT_SHELL_SX = {
  height: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
} as const;
const FULL_HEIGHT_COLUMN_SX = {
  ...FILL_PARENT_SX,
  display: 'flex',
  flexDirection: 'column',
} as const;
// Rendered on every route so crossing the md breakpoint restyles this element instead of
// remounting the page subtree under it.
const PASSTHROUGH_SX = { display: 'contents' } as const;

interface AppLayoutProps {
  children?: React.ReactNode;
}

export const AppLayout: FC<AppLayoutProps> = ({ children }): JSX.Element => {
  const authContext = useAuthContext();
  const [querySearch, updateSearch, deleteSearch] = useQueryParamState('globalSearchQuery');
  const { openSnackbar } = useSnackbar();
  const [leftNavDrawerOpen, setLeftNavDrawerOpen] = useState(false);

  const [sidebarMode, setSidebarModeState] = useState<SidebarMode>(() => readSidebarMode());
  const [manualCollapsed, setManualCollapsed] = useState<boolean>(() => readManualCollapsed());
  const location = useLocation();
  const showingCampaign = isInsideCampaign(location.pathname);
  const sidebarCollapsed = !isSidebarPinned(sidebarMode, showingCampaign, manualCollapsed);
  const sidebarCanPeek = canSidebarPeek(sidebarMode, showingCampaign);
  const fullHeight = useFullHeightRoute();

  const setSidebarMode = useCallback((mode: SidebarMode) => {
    setSidebarModeState(mode);
    writeSidebarMode(mode);
  }, []);

  const toggleManualCollapsed = useCallback(() => {
    setManualCollapsed((prev) => {
      writeManualCollapsed(!prev);
      return !prev;
    });
  }, []);

  const searchOpen = typeof querySearch !== 'undefined';

  const toggleLeftNavDrawer = useCallback(() => setLeftNavDrawerOpen((prev) => !prev), []);

  const handleLogout = async () => {
    try {
      deleteLocalStorageAuth();
      await signOut();
    } catch (e) {
      console.log('error logging out', e);
    }
  };

  const openSearch = useCallback(() => updateSearch('', false), [updateSearch]);

  const closeSearch = () => {
    deleteSearch(false);
  };

  const searchChange = (search: string) => {
    updateSearch(search);
  };

  const copyAuthToken = async () => {
    const session = await fetchAuthSession();
    await navigator.clipboard.writeText(session.tokens?.accessToken?.toString() || '');
    openSnackbar({ message: 'Auth token copied to clipboard', severity: 'success' });
  };

  const handleKeypress = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.code === 'KeyK') {
      openSearch();
    } else if ((e.metaKey || e.ctrlKey) && e.code === 'KeyJ') {
      copyAuthToken();
    }
  };

  useEffect(() => {
    if (!authContext.isInternal) {
      return;
    }
    document.addEventListener('keydown', handleKeypress);
    return () => document.removeEventListener('keydown', handleKeypress);
  }, []);

  // Seven components read this context and three rebuild the whole nav tree, so a fresh object
  // each render would re-render them all on any unrelated AppLayout change.
  const pageContextValue = useMemo(
    () => ({
      openSearch,
      leftNavDrawerOpen,
      toggleLeftNavDrawer,
      sidebarCollapsed,
      sidebarMode,
      setSidebarMode,
      sidebarCanPeek,
      toggleManualCollapsed,
      fullHeight,
    }),
    [
      openSearch,
      leftNavDrawerOpen,
      toggleLeftNavDrawer,
      sidebarCollapsed,
      sidebarMode,
      setSidebarMode,
      sidebarCanPeek,
      toggleManualCollapsed,
      fullHeight,
    ],
  );

  return (
    <Box sx={fullHeight ? FULL_HEIGHT_SHELL_SX : undefined}>
      <Box sx={fullHeight ? FULL_HEIGHT_COLUMN_SX : undefined}>
        <PageContext.Provider value={pageContextValue}>
          <AppBar
            handleDrawerToggle={toggleLeftNavDrawer}
            handleSearchToggle={searchOpen ? closeSearch : openSearch}
            handleLogout={handleLogout}
          />
          {/* Off a full-height route this collapses away, leaving the page's own flex row as
              the shell's direct child exactly as before. */}
          <Box sx={fullHeight ? FULL_HEIGHT_ROW_SX : PASSTHROUGH_SX}>{children}</Box>
        </PageContext.Provider>
      </Box>

      {authContext.isInternal && (
        <Search
          open={searchOpen}
          closeSearch={closeSearch}
          initialSearch={querySearch || ''}
          onSearchChange={searchChange}
        />
      )}
    </Box>
  );
};
